/**
 * advance-ai-standing-orders.ts — v4M8.8a Standing-order resolution
 * helper per `docs/AI.md §5.10`.
 *
 * Resolves the default standing order for a ship from its chassis +
 * the empire's composed `behaviour.standingOrders` row (per archetype
 * — Aggressor / Defender / Expander / Explorer / Diplomat /
 * Researcher). Returns the raw order string from the row so the
 * v4M8.8b dispatch wiring can translate it into the appropriate
 * v3M22 command (`explore`, `patrol-*`, `guard-*`, or `idle` for
 * no-op).
 *
 * Chassis classifier matches the buckets used by the StandingOrders
 * data shape:
 *   - scout         → `scoutDefault`
 *   - combatSmall   → `combatSmallDefault`        (light + mid combat)
 *   - combatCapital → `combatCapitalDefault`      (capital combat)
 *   - constructor   → `constructorDefault`
 *   - coloniser     → `coloniserDefault`
 *   - other         → null (space structures, squadrons — no order)
 *
 * Mirrors the regex set in `render/ship-draw.ts:chassisRoleFor` but
 * kept local to avoid a render-layer dependency from a sim file
 * (same convention used by `advance-ai-cloaking.ts:classifyChassisForCloak`
 * and `advance-ai.ts:isCombatChassisForRecall`). The combat-small vs
 * combat-capital split mirrors chassisRoleFor's 'combat'/'combat-light'
 * vs 'capital' classification: Light (Corvette/Fighter/Infiltrator)
 * + Mid (Cruiser/Praetorian/Warship/Destroyer) → small; Capital
 * (Battleship/Dreadnought/Colossus/Carrier) → capital.
 *
 * v4M8.8a strict-parity: helper exists + tested, but no consumer
 * reads it yet. v4M8.8b adds the per-tick application pass that
 * actually issues v3M22 commands from the resolved orders.
 */

import type {
  EffectiveBehaviour,
  ShipInstance,
  ShipTemplate,
} from '../state/types/index';

/**
 * Standing-order bucket a chassis falls into. Matches the field
 * names on `StandingOrdersRow` 1:1 so the field lookup is trivial.
 * `'other'` covers space structures (Warp Gate / Minefield / Buoy
 * / etc), Carrier-squadrons (synthetic chassis ending in 'Squadron'),
 * and any unrecognised chassis — none get standing orders.
 */
export type StandingOrderBucket =
  | 'scout'
  | 'combatSmall'
  | 'combatCapital'
  | 'constructor'
  | 'coloniser'
  | 'other';

/**
 * Bucket `chassis` into one of the six standing-order categories.
 * Pure function — drives off the chassis name only, no state.
 *
 * Order of regex tests matters: squadron suffix is checked first so
 * `'Corvette Squadron'` doesn't fall into the Corvette combat-light
 * branch; space-structure word-boundary check comes near the end so
 * structure-class names like `'Buoy'` / `'Warp Gate'` aren't
 * misclassified as combat. Unknown chassis fall through to `'other'`
 * — safer than guessing a combat default for unseen chassis names.
 */
export function classifyChassisForStandingOrder(
  chassis: string,
): StandingOrderBucket {
  // Carrier squadrons (synthetic chassis names ending in "Squadron")
  // — checked first so 'Corvette Squadron' doesn't match the
  // Corvette combat-light branch below.
  if (/Squadron$/i.test(chassis)) return 'other';
  // Scouts: Scout (Terran) / Ranger (Kargath) / Probe (Reman).
  if (/^(Scout|Ranger|Probe)$/i.test(chassis)) return 'scout';
  // Coloniser family — Coloniser / Colonyser (race spelling variants).
  if (/Colon[iy]ser/i.test(chassis)) return 'coloniser';
  // Constructor family — Engineer / Constructor / Mechanic.
  if (/^(Engineer|Constructor|Mechanic)$/i.test(chassis)) return 'constructor';
  // Capital combat — Battleship / Dreadnought / Colossus / Carrier
  // (chassisRoleFor's 'capital' bucket; Class 4-6).
  if (/^(Battleship|Dreadnought|Colossus|Carrier)$/i.test(chassis)) {
    return 'combatCapital';
  }
  // Small combat — light (Corvette/Fighter/Infiltrator, Class 1) +
  // mid (Cruiser/Praetorian/Warship/Destroyer, Class 2-3). The
  // standing-orders row only splits at the capital boundary, so
  // light + mid share the `combatSmallDefault` field.
  if (
    /^(Corvette|Fighter|Infiltrator|Cruiser|Praetorian|Warship|Destroyer)$/i.test(
      chassis,
    )
  ) {
    return 'combatSmall';
  }
  // Space structures — Buoy / Platform / Yard / Outpost / Refinery
  // / Gate / Minefield. Word-boundary check (MFix11.0) so 'Gate'
  // doesn't match inside 'Frigate'.
  if (/\b(Buoy|Platform|Yard|Outpost|Refinery|Gate|Minefield)\b/i.test(chassis)) {
    return 'other';
  }
  // Unknown chassis — no standing order (safer than guessing).
  return 'other';
}

/**
 * Resolve the standing-order string for `ship` under the composed
 * `behaviour`. Returns `null` when:
 *   - The chassis bucket is `'other'` (no standing-order field
 *     applies — space structures, squadrons, unrecognised chassis).
 *   - The matching field on `behaviour.standingOrders` is empty
 *     (the data treats `""` as "no order" — distinct from `"idle"`
 *     which is an explicit "stay idle" directive).
 *
 * `template` is currently unused but kept in the signature so v4M8.8b
 * can extend the resolver (e.g. to skip standing orders for ships
 * that lack the `canPatrol` template flag, if one is added later)
 * without changing call sites.
 *
 * Pure — no state mutation, no random draws. Deterministic for
 * given inputs.
 */
export function resolveStandingOrderForShip(
  behaviour: EffectiveBehaviour,
  ship: ShipInstance,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _template: ShipTemplate,
): string | null {
  const bucket = classifyChassisForStandingOrder(ship.chassis);
  if (bucket === 'other') return null;
  const row = behaviour.standingOrders;
  let order: string;
  switch (bucket) {
    case 'scout':
      order = row.scoutDefault;
      break;
    case 'combatSmall':
      order = row.combatSmallDefault;
      break;
    case 'combatCapital':
      order = row.combatCapitalDefault;
      break;
    case 'constructor':
      order = row.constructorDefault;
      break;
    case 'coloniser':
      order = row.coloniserDefault;
      break;
  }
  if (!order || order.length === 0) return null;
  return order;
}
